import { API_BASE_URL } from "../config/apiConfig";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LockKeyhole,
  Mail,
  ShieldCheck,
} from "lucide-react";

import "../styles/AdminLogin.css";

function AdminLogin() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    email: "",
    password: "",
  });

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState("");

  const handleChange = (event) => {
    const { name, value } =
      event.target;

    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleSubmit = async (
    event
  ) => {
    event.preventDefault();

    setError("");

    if (
      !form.email.trim() ||
      !form.password
    ) {
      setError(
        "Email and password are required."
      );

      return;
    }

    try {
      setLoading(true);

      const response = await fetch(
        `${API_BASE_URL}/auth/login`,
        {
          method: "POST",

          headers: {
            "Content-Type":
              "application/json",
          },

          body: JSON.stringify({
            email:
              form.email.trim(),
            password:
              form.password,
          }),
        }
      );

      const data =
        await response.json();

      if (!response.ok) {
        throw new Error(
          data?.message ||
            "Unable to sign in."
        );
      }

      /*
      ========================================
      ADMIN ROLE CHECK
      ========================================
      */

      if (
        data?.user?.role !==
        "admin"
      ) {
        throw new Error(
          "This account does not have administrator access."
        );
      }

      /*
      ========================================
      GET TOKEN
      ========================================

      Supports the most likely response
      names while we standardize auth.
      */

      const token =
        data?.token ||
        data?.accessToken ||
        data?.access_token;

      if (!token) {
        console.error(
          "Login response:",
          data
        );

        throw new Error(
          "Login succeeded but no authentication token was returned."
        );
      }

      /*
      ========================================
      SAVE AUTHENTICATION

      IMPORTANT:
      api.js currently reads zethAuthToken.
      ========================================
      */

      localStorage.setItem(
        "zethAuthToken",
        token
      );

      localStorage.setItem(
        "zethUser",
        JSON.stringify(
          data.user
        )
      );

      /*
      Remove possible stale session token.
      */

      sessionStorage.removeItem(
        "zethAuthToken"
      );

      sessionStorage.removeItem(
        "zethUser"
      );

      navigate(
        "/admin/dashboard",
        {
          replace: true,
        }
      );
    } catch (loginError) {
      console.error(
        "Admin login error:",
        loginError
      );

      localStorage.removeItem(
        "zethAuthToken"
      );

      localStorage.removeItem(
        "zethUser"
      );

      setError(
        loginError.message ||
          "Unable to sign in."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="admin-login-page">
      <section className="admin-login-brand">
        <div className="admin-login-brand-content">
          <p className="admin-login-eyebrow">
            ZETH HEALTH STAFFING
          </p>

          <h1>
            Workforce management,
            built for healthcare.
          </h1>

          <p className="admin-login-brand-copy">
            Manage professionals,
            facilities, assignments,
            credentials and compliance
            from one secure
            administration portal.
          </p>

          <div className="admin-login-security">
            <ShieldCheck
              size={22}
            />

            <span>
              Authorized Zeth Health
              administrators only
            </span>
          </div>
        </div>
      </section>

      <section className="admin-login-panel">
        <div className="admin-login-card">
          <div className="admin-login-icon">
            <LockKeyhole
              size={28}
            />
          </div>

          <p className="admin-login-small-title">
            ADMINISTRATION
          </p>

          <h2>
            Admin Login
          </h2>

          <p className="admin-login-description">
            Sign in to access the Zeth
            Health administration
            portal.
          </p>

          {error && (
            <div className="admin-login-error">
              {error}
            </div>
          )}

          <form
            onSubmit={
              handleSubmit
            }
            className="admin-login-form"
          >
            <label>
              <span>
                Email address
              </span>

              <div className="admin-login-input">
                <Mail
                  size={19}
                />

                <input
                  type="email"
                  name="email"
                  value={
                    form.email
                  }
                  onChange={
                    handleChange
                  }
                  placeholder="admin@zethhealth.ca"
                  autoComplete="email"
                  required
                />
              </div>
            </label>

            <label>
              <span>
                Password
              </span>

              <div className="admin-login-input">
                <LockKeyhole
                  size={19}
                />

                <input
                  type="password"
                  name="password"
                  value={
                    form.password
                  }
                  onChange={
                    handleChange
                  }
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  required
                />
              </div>
            </label>

            <button
              type="submit"
              className="admin-login-button"
              disabled={
                loading
              }
            >
              {loading
                ? "Signing in..."
                : "Sign in to Admin Portal"}
            </button>
          </form>

          <p className="admin-login-footer">
            Zeth Health Staffing
            Administration
          </p>
        </div>
      </section>
    </main>
  );
}

export default AdminLogin;
